import { AfterViewInit, Component, computed, ElementRef, inject, input, OnDestroy, viewChild, ViewEncapsulation } from '@angular/core';
import { MosDevice } from '@common/data-models/service/mos-device';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { AppTheme } from '@src/suite/app.theme';
import { DRAGTYPES } from '@src/suite/common/enums/enums';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { SidecarEfffectActions } from '@src/suite/common/store/fullscreen';
import { selectSidecarRoverAIPlugIn } from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { MosActions } from '@src/suite/common/store/mos/actions/mos.actions';
import { fireCustomEvent, getElementFromCoordinates } from '@src/suite/common/utils/misc-utils';
import { getMosCommand } from '@src/suite/common/utils/mos-utlis';
import { Subject, takeUntil } from 'rxjs';

import { UnsafePipe } from '../../pipes/unsafe';

const defaultSettingsPrefix = 'URL=';

@Component({
    selector: 'st-rover-ai-full-screen',
    imports: [UnsafePipe],
    templateUrl: './st-rover-ai-full-screen.component.html',
    styleUrl: './st-rover-ai-full-screen.component.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rover-ai-full-screen',
    },
})
export class StRoverAIFullScreenComponent implements OnDestroy, AfterViewInit {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    mosEventService = inject(MosEventService);
    appTheme = inject(AppTheme);

    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    readonly mosDevice = computed(() => {
        const mosDevice = this.store.selectSignal(selectSidecarRoverAIPlugIn)();
        return mosDevice as MosDevice;
    });

    readonly mosUrl = computed(() => {
        if (this.mosDevice()) {
            this.origin = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}${window.location.hash ? window.location.hash : ''}`;
            const localMosUrl =
                (this.mosDevice().DefaultSettings?.startsWith(defaultSettingsPrefix) ? this.mosDevice().DefaultSettings?.substring(defaultSettingsPrefix.length) : this.mosDevice().DefaultSettings) ||
                '';
            return localMosUrl.indexOf('?') === -1 ? `${localMosUrl}?origin=${this.origin}` : `${localMosUrl}&origin=${this.origin}`;
        } else {
            return '';
        }
    });

    destroyed$ = new Subject<void>();

    readonly roverIframe = viewChild<ElementRef<HTMLIFrameElement>>('roverIframe');

    private origin: string;

    constructor() {
        this.mosEventService
            .onMosClientCommand()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(mos => {
                const roverIframe = this.roverIframe();
                if (roverIframe?.nativeElement?.contentWindow && mos.sidecar === this.sidecarMenu() && mos.mosPluginId === this.mosDevice()?.ID) {
                    if (mos?.command?.length > 0 && mos?.command.includes('<mos>')) {
                        roverIframe.nativeElement.contentWindow.postMessage(mos.command, '*');
                    }
                }
            });
    }

    private mosHandler;

    ngAfterViewInit() {
        this.mosHandler = this.mosMsgFromPlugin.bind(this);
        window.addEventListener('message', this.mosHandler, false);
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.store.dispatch(SidecarEfffectActions.resetMosState({ sidecar: this.sidecarMenu() }));
        window.removeEventListener('message', this.mosHandler, false);
    }

    onMosChange(event: any) {
        this.store.dispatch(SidecarEfffectActions.setCurrentMosPlugin({ mosPluginId: event.id, sidecarMenu: this.sidecarMenu() }));
    }

    mosMsgFromPlugin(event: MessageEvent) {
        // Handle drag & drop events
        if (event?.data?.type === DRAGTYPES.MosPluginDragEnd) {
            const roverIframe = this.roverIframe();
            if (roverIframe && roverIframe.nativeElement) {
                const rect = roverIframe.nativeElement.getBoundingClientRect();
                const coord = {
                    x: rect.x + event.data.coord.pageX,
                    y: rect.y + event.data.coord.pageY,
                };
                const target = getElementFromCoordinates(coord.x, coord.y);
                if (target) {
                    fireCustomEvent('drop', target, event, coord.x, coord.y);
                }
            }

            return;
        }
        if (event?.data?.length > 0) {
            // Handle the Message
            // To Reply, issue a postMessage on the event source.
            const mosCommandType = getMosCommand(event.data);
            this.store.dispatch(MosActions.mosServerCommandSend({ command: event.data, mosPluginId: this.mosDevice()?.ID, sidecar: this.sidecarMenu(), mosCommandType }));
        }
    }

    onMosLoad() {
        const roverIframe = this.roverIframe();
        if (roverIframe?.nativeElement.contentWindow) {
            const darkModeAttribute = this.appTheme.darkMode() ? 'dark' : 'light';
            roverIframe.nativeElement.contentWindow.postMessage({ name: 'dark-mode', value: darkModeAttribute }, '*');
        }
    }
}
